/**
 * Drip reels — reliable muted autoplay + buffer-aware left advance
 */
(function () {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class DripIgReels {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-dir-stage]');
      this.dotsWrap = root.querySelector('[data-dir-dots]');
      this.items = Array.from(root.querySelectorAll('[data-dir-item]'));
      this.index = 0;
      this.timer = 0;
      this.spinTimer = 0;
      this.chromeTimer = 0;
      this.dragging = false;
      this.scrubbing = false;
      this.startX = 0;
      this.deltaX = 0;
      this.moved = false;
      this.inView = false;
      this.userHold = false;
      this.autoplay = true;
      this.interval = Math.max(5000, parseInt(root.dataset.interval || '7000', 10) || 7000);
      this.playToken = 0;
      this.armed = new WeakSet();
      this.advanceLock = false;

      if (!this.stage || this.items.length === 0) return;

      this.bind();
      this.buildDots();
      this.prepVideos();
      this.render(true);

      // Don't wait on IO — kick active clip immediately if already painted
      requestAnimationFrame(() => {
        this.armNeighbors(this.index);
        this.playActive(true);
      });
    }

    bind() {
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onVisibility = this.onVisibility.bind(this);

      this.stage.addEventListener('pointerdown', this.onPointerDown);
      this.stage.addEventListener('keydown', this.onKey);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.items.forEach((item) => this.bindItemControls(item));

      if (!('IntersectionObserver' in window)) {
        this.inView = true;
        this.startAutoplay();
        this.playActive(true);
        return;
      }

      this.io = new IntersectionObserver(
        (entries) => {
          const hit = entries.some((en) => en.isIntersecting && en.intersectionRatio > 0.05);
          this.inView = hit;
          if (hit) {
            this.armNeighbors(this.index);
            this.playActive(true);
            this.startAutoplay();
          } else {
            this.pauseAutoplay();
            this.pauseInactive(true);
          }
        },
        { threshold: [0, 0.05, 0.2], rootMargin: '200px 0px' }
      );
      this.io.observe(this.root);
    }

    bindItemControls(item) {
      const video = item.querySelector('[data-dir-video]');
      const tap = item.querySelector('[data-dir-tap]');
      const range = item.querySelector('[data-dir-range]');
      const bar = item.querySelector('[data-dir-controls]');

      if (tap) {
        tap.addEventListener('click', (e) => {
          e.stopPropagation();
          if (this.moved) return;
          this.togglePlay(item);
        });
      }

      if (range) {
        const stop = (e) => e.stopPropagation();
        range.addEventListener('pointerdown', (e) => {
          stop(e);
          this.scrubbing = true;
          this.userHold = true;
          this.pauseAutoplay();
        });
        range.addEventListener('pointerup', stop);
        range.addEventListener('click', stop);
        range.addEventListener('input', () => {
          if (!video || !isFinite(video.duration) || video.duration <= 0) return;
          try {
            video.currentTime = (Number(range.value) / 1000) * video.duration;
          } catch (_) {}
        });
        range.addEventListener('change', () => {
          this.scrubbing = false;
          this.userHold = false;
          if (video && video.paused) this.safePlay(video);
          this.startAutoplay();
        });
      }

      if (bar) bar.addEventListener('pointerdown', (e) => e.stopPropagation());

      if (!video) return;

      video.addEventListener('timeupdate', () => {
        if (!this.scrubbing) this.updateProgress(item);
      });
      video.addEventListener('loadedmetadata', () => this.updateProgress(item));
      video.addEventListener('ended', () => {
        if (item.classList.contains('is-active')) this.goNext(true);
      });
      video.addEventListener('playing', () => {
        item.classList.remove('is-paused', 'is-spin');
        video.classList.add('is-ready');
      });
      video.addEventListener('canplay', () => {
        video.classList.add('is-ready');
        item.classList.remove('is-spin');
        if (item.classList.contains('is-active') && this.inView && !this.userHold && video.paused) {
          this.safePlay(video);
        }
      });
      video.addEventListener('waiting', () => this.maybeSpin(item));
      video.addEventListener('stalled', () => {
        if (item.classList.contains('is-active')) this.safePlay(video);
      });
      video.addEventListener('pause', () => {
        if (item.classList.contains('is-active')) item.classList.add('is-paused');
      });
      video.addEventListener('play', () => item.classList.remove('is-paused'));
      video.addEventListener('error', () => {
        item.classList.remove('is-spin');
        video.classList.add('is-error');
      });
    }

    maybeSpin(item) {
      clearTimeout(this.spinTimer);
      // Only show spinner if still waiting after 500ms (avoid flash)
      this.spinTimer = setTimeout(() => {
        const video = item.querySelector('[data-dir-video]');
        if (
          item.classList.contains('is-active') &&
          video &&
          !video.ended &&
          (video.readyState < 3 || video.paused === false)
        ) {
          // readyState < HAVE_FUTURE_DATA while supposed to play
          if (video.readyState < 3) item.classList.add('is-spin');
        }
      }, 500);
    }

    togglePlay(item) {
      const video = item.querySelector('[data-dir-video]');
      if (!video) return;
      this.armVideo(video);
      if (video.paused) {
        this.userHold = false;
        this.safePlay(video).then(() => this.startAutoplay());
      } else {
        video.pause();
        this.userHold = true;
        this.pauseAutoplay();
        item.classList.add('is-paused');
      }
    }

    updateProgress(item) {
      const video = item.querySelector('[data-dir-video]');
      const range = item.querySelector('[data-dir-range]');
      if (!video || !range || this.scrubbing) return;
      const d = video.duration;
      if (!isFinite(d) || d <= 0) return;
      range.value = String(Math.round(((video.currentTime || 0) / d) * 1000));
    }

    prepVideos() {
      this.items.forEach((item, i) => {
        const video = item.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.autoplay = i === 0;
        video.loop = false;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        try {
          video.disableRemotePlayback = true;
        } catch (_) {}

        if (video.getAttribute('src') || video.querySelector('source[src]')) {
          this.armed.add(video);
        }
        item.classList.add('is-paused');
      });
    }

    /** Attach src once — never call load() again (wipes buffer) */
    armVideo(video) {
      if (!video || this.armed.has(video)) return;
      const ds = video.getAttribute('data-src');
      if (ds && !video.getAttribute('src')) {
        video.setAttribute('src', ds);
        video.preload = 'auto';
      }
      this.armed.add(video);
    }

    armNeighbors(center) {
      const n = this.items.length;
      if (!n) return;
      // Active + next only — don't starve the playing clip
      [0, 1].forEach((off) => {
        const idx = (center + off + n) % n;
        const video = this.items[idx]?.querySelector('video');
        if (!video) return;
        this.armVideo(video);
        video.preload = 'auto';
      });
      // Soft metadata for previous (swipe back)
      const prev = this.items[(center - 1 + n) % n]?.querySelector('video');
      if (prev) {
        this.armVideo(prev);
        if (prev.preload === 'none') prev.preload = 'metadata';
      }
      this.hydratePosters();
    }

    hydratePosters() {
      this.items.forEach((item) => {
        const img = item.querySelector('[data-dir-poster]');
        if (!img) return;
        const ds = img.getAttribute('data-src');
        if (ds && !img.getAttribute('src')) {
          const srcset = img.getAttribute('data-srcset');
          const sizes = img.getAttribute('data-sizes');
          if (srcset) img.setAttribute('srcset', srcset);
          if (sizes) img.setAttribute('sizes', sizes);
          img.setAttribute('src', ds);
        }
      });
    }

    isReady(video) {
      return !!(video && video.readyState >= 3);
    }

    safePlay(video) {
      if (!video) return Promise.resolve();
      this.armVideo(video);
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      const token = ++this.playToken;

      const attempt = () => {
        if (token !== this.playToken) return Promise.resolve();
        const p = video.play();
        if (p && typeof p.then === 'function') {
          return p.catch(() => {
            video.muted = true;
            return video.play().catch(() => {});
          });
        }
        return Promise.resolve();
      };

      if (video.readyState >= 2) return attempt();

      return new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          video.removeEventListener('canplay', onReady);
          video.removeEventListener('loadeddata', onReady);
          attempt().then(resolve);
        };
        const onReady = () => finish();
        video.addEventListener('canplay', onReady, { once: true });
        video.addEventListener('loadeddata', onReady, { once: true });
        // Nudge decode without full reset
        try {
          if (video.preload !== 'auto') video.preload = 'auto';
        } catch (_) {}
        setTimeout(finish, 1200);
      });
    }

    playActive(force) {
      if (document.hidden) return;
      const item = this.items[this.index];
      const video = item?.querySelector('video');
      if (!video) return;
      if (this.userHold && video.paused && !force) return;

      this.armNeighbors(this.index);
      this.maybeSpin(item);

      this.items.forEach((other, i) => {
        if (i === this.index) return;
        const v = other.querySelector('video');
        if (v && !v.paused) v.pause();
      });

      this.safePlay(video).then(() => {
        item.classList.remove('is-spin', 'is-paused');
        this.updateProgress(item);
      });
    }

    pauseInactive(keepActivePaused) {
      this.items.forEach((item, i) => {
        const video = item.querySelector('video');
        if (!video) return;
        if (keepActivePaused || i !== this.index) {
          video.pause();
        }
      });
    }

    buildDots() {
      if (!this.dotsWrap || this.items.length < 2) {
        if (this.dotsWrap) this.dotsWrap.innerHTML = '';
        return;
      }
      this.dotsWrap.innerHTML = '';
      this.items.forEach((_, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'drip-reels__dot' + (i === 0 ? ' is-active' : '');
        btn.setAttribute('aria-label', 'Video ' + (i + 1));
        btn.addEventListener('click', () => {
          this.userHold = false;
          this.index = i;
          this.render(false);
          this.playActive(true);
          this.startAutoplay();
        });
        this.dotsWrap.appendChild(btn);
      });
    }

    goNext(fromEnded) {
      if (this.items.length < 2 || this.advanceLock) return;
      const next = (this.index + 1) % this.items.length;
      const nextVideo = this.items[next]?.querySelector('video');

      this.armVideo(nextVideo);
      if (nextVideo) nextVideo.preload = 'auto';

      // Don't advance into an empty buffer — wait briefly then go anyway
      if (nextVideo && !this.isReady(nextVideo) && !fromEnded) {
        this.advanceLock = true;
        const wait = (tries) => {
          if (this.isReady(nextVideo) || tries <= 0) {
            this.advanceLock = false;
            this.userHold = false;
            this.index = next;
            this.render(false);
            this.playActive(true);
            this.startAutoplay();
            return;
          }
          setTimeout(() => wait(tries - 1), 200);
        };
        wait(12); // ~2.4s max wait for next buffer
        return;
      }

      this.userHold = false;
      this.index = next;
      this.render(false);
      this.playActive(true);
      this.startAutoplay();
    }

    startAutoplay() {
      this.pauseAutoplay();
      if (REDUCE || !this.autoplay || this.items.length < 2) return;
      if (document.hidden || this.userHold || this.scrubbing) return;
      this.timer = window.setInterval(() => {
        if (this.dragging || this.scrubbing || this.userHold || this.advanceLock) return;
        // Only tick-advance if section is on screen
        if (!this.inView) return;
        this.goNext(false);
      }, this.interval);
    }

    pauseAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = 0;
      }
    }

    onVisibility() {
      if (document.hidden) {
        this.pauseAutoplay();
        this.pauseInactive(true);
      } else {
        this.playActive(true);
        this.startAutoplay();
      }
    }

    onKey(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goNext(true);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.userHold = false;
        this.index = (this.index - 1 + this.items.length) % this.items.length;
        this.render(false);
        this.playActive(true);
        this.startAutoplay();
      } else if (e.key === ' ') {
        e.preventDefault();
        const active = this.items[this.index];
        if (active) this.togglePlay(active);
      }
    }

    onPointerDown(e) {
      if (e.button != null && e.button !== 0) return;
      if (e.target.closest('[data-dir-controls], [data-dir-range]')) return;
      this.dragging = true;
      this.moved = false;
      this.startX = e.clientX;
      this.deltaX = 0;
      this.stage.classList.add('is-dragging');
      try {
        this.stage.setPointerCapture(e.pointerId);
      } catch (_) {}
      this.stage.addEventListener('pointermove', this.onPointerMove);
      this.stage.addEventListener('pointerup', this.onPointerUp);
      this.stage.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(e) {
      if (!this.dragging) return;
      this.deltaX = e.clientX - this.startX;
      if (Math.abs(this.deltaX) > 10) this.moved = true;
      const active = this.items[this.index];
      if (active && Math.abs(this.deltaX) < 140) {
        active.style.transform =
          'translate3d(calc(-50% + ' + (this.deltaX * 0.35).toFixed(1) + 'px),0,40px) rotateY(0deg) scale(1)';
      }
    }

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.stage.classList.remove('is-dragging');
      this.stage.removeEventListener('pointermove', this.onPointerMove);
      this.stage.removeEventListener('pointerup', this.onPointerUp);
      this.stage.removeEventListener('pointercancel', this.onPointerUp);

      if (this.deltaX < -56) {
        this.goNext(true);
      } else if (this.deltaX > 56) {
        this.userHold = false;
        this.index = (this.index - 1 + this.items.length) % this.items.length;
        this.render(false);
        this.playActive(true);
        this.startAutoplay();
      } else {
        const active = this.items[this.index];
        if (active) {
          active.style.transform = '';
          this.render(false);
        }
      }
    }

    render(instant) {
      const n = this.items.length;
      this.items.forEach((item, i) => {
        let dist = i - this.index;
        if (dist > n / 2) dist -= n;
        if (dist < -n / 2) dist += n;

        item.classList.toggle('is-active', dist === 0);
        item.classList.toggle('is-near', Math.abs(dist) === 1);

        const abs = Math.abs(dist);
        let x = 0;
        let z = 0;
        let rot = 0;
        let scale = 1;
        let opacity = 1;
        let filter = 'none';

        if (abs === 0) {
          x = 0;
          z = 48;
          scale = 1;
          opacity = 1;
        } else if (abs === 1) {
          x = dist * 78;
          z = -40;
          rot = dist * -18;
          scale = 0.86;
          opacity = 0.78;
          filter = 'brightness(0.75)';
        } else if (abs === 2) {
          x = dist * 118;
          z = -110;
          rot = dist * -28;
          scale = 0.72;
          opacity = 0.35;
          filter = 'brightness(0.55)';
        } else {
          x = dist * 140;
          z = -160;
          rot = dist * -32;
          scale = 0.62;
          opacity = 0;
        }

        if (instant) item.style.transition = 'none';
        item.style.opacity = String(opacity);
        item.style.filter = filter;
        item.style.zIndex = String(20 - abs);
        const t =
          'translate3d(calc(-50% + ' + x + '%), 0, ' + z + 'px) rotateY(' + rot + 'deg) scale(' + scale + ')';
        item.style.webkitTransform = t;
        item.style.transform = t;
        if (instant) {
          void item.offsetWidth;
          item.style.transition = '';
        }
      });

      if (this.dotsWrap) {
        Array.from(this.dotsWrap.children).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this.index);
        });
      }

      this.armNeighbors(this.index);
    }
  }

  function init(root) {
    if (!root || root.__dirInit) return;
    root.__dirInit = true;
    new DripIgReels(root);
  }

  function scan() {
    document.querySelectorAll('[data-drip-reels]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target?.querySelector?.('[data-drip-reels]') || e.target;
    if (el?.matches?.('[data-drip-reels]')) {
      el.__dirInit = false;
      init(el);
    } else {
      scan();
    }
  });
})();
